import { defineStore } from "pinia";
import { RouteLocation } from "vue-router";
import { WEBSOCKET_READY_STATE } from "@supplier/meta";
import router from "@/routers";
import { useUserStore } from "@/stores/user";
import { useKeepAliveStore } from "@/stores/keepAlive";
import { MAX_TAG_NUMBER } from "@/config";
import { getUserInfo } from "@/utils/common";
import enterChangeDialog from "@/components/EnterChangeDialog/index";

/* tabsMenuProps */
export interface TabsMenuProps {
    icon?: string;
    title: string;
    path: string;
    name: string;
    close: boolean;
    isKeepAlive?: boolean;
}

/* TabsState */
export interface TabsState {
    nextReload: boolean;
    tabsMenuList: TabsMenuProps[];
}

let connected = false;
let failure = false;
let WS: WebSocket | undefined;
let timer: ReturnType<typeof setInterval> | undefined;
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
let reconnectRetries = 0;
let currentToken = "";
let manualClose = false;
let socketOpenedAt = 0;

interface CloseDetailTabsByDeletedIdOptions {
    names: string | string[];
    id: string | number;
    queryKeys?: string[];
}

// WebSocket 重连上限配置为模块级，避免每次 connectWebsocket 调用都重置计数。
const MAX_RECONNECT_RETRIES = 5;
const RECONNECT_INTERVAL = 3000;
// 握手后很快被服务端关闭的连接视为不可用连接，不进入自动重连，避免不断新增 WS 请求。
const STABLE_CONNECTION_DURATION = 5000;

const isSameDeletedDetailTab = (tab: TabsMenuProps, options: CloseDetailTabsByDeletedIdOptions) => {
    const names = Array.isArray(options.names) ? options.names : [options.names];
    if (!tab.close || !names.includes(tab.name)) return false;

    const deletedId = String(options.id);
    const queryKeys = options.queryKeys?.length ? options.queryKeys : ["id"];
    const url = new URL(tab.path, window.location.origin);
    const hasSameQueryId = queryKeys.some(key => url.searchParams.get(key) === deletedId);
    const hasSamePathId = url.pathname.split("/").some(pathItem => decodeURIComponent(pathItem) === deletedId);

    return hasSameQueryId || hasSamePathId;
};

export const useTabsStore = defineStore("zhongsheng-tabs", {
    state: (): TabsState => ({
        nextReload: true,
        tabsMenuList: []
    }),
    actions: {
        // Add Tabs
        async addTabs(tabItem: TabsMenuProps) {
            if (this.tabsMenuList.every(item => item.path !== tabItem.path)) {
                if (this.tabsMenuList.some(item => item.name === tabItem.name)) {
                    const index = this.tabsMenuList.findIndex(item => item.name === tabItem.name);
                    this.tabsMenuList[index] = tabItem;
                } else {
                    this.tabsMenuList.push(tabItem);
                }
            }
            if (this.tabsMenuList.length > MAX_TAG_NUMBER) await this.removeLimitExceededTabs();
        },
        // Remove Tabs
        async removeTabs(tabPath: string, callback?: () => void, isCurrent: boolean = true, closeCurrent: boolean = false) {
            const tabsMenuList = this.tabsMenuList;
            if (isCurrent) {
                tabsMenuList.forEach((item, index) => {
                    if (item.path !== tabPath) return;
                    const nextTab = tabsMenuList[index + 1] || tabsMenuList[index - 1];
                    if (!nextTab) return;
                    if (!callback) router.push(nextTab.path);
                });
            }
            if (!callback && closeCurrent) await router.push(tabsMenuList[0].path);
            this.tabsMenuList = tabsMenuList.filter(item => item.path !== tabPath);
            if (callback) callback();
        },
        // 按名称删 tab 新
        async removeTabsByName(tabName: string, callback?: () => void, isCurrent: boolean = true, closeCurrent: boolean = false) {
            const tabsMenuList = this.tabsMenuList;
            if (isCurrent) {
                tabsMenuList.forEach((item, index) => {
                    if (item.name !== tabName) return;
                    const nextTab = tabsMenuList[index + 1] || tabsMenuList[index - 1];
                    if (!nextTab) return;
                    if (!callback) router.push(nextTab.path);
                });
            }
            if (!callback && closeCurrent) await router.push(tabsMenuList[0].path);
            this.tabsMenuList = tabsMenuList.filter(item => item.name !== tabName);
            if (callback) callback();
        },
        // 列表删除成功后，按详情路由与业务 id 精准关闭已打开的详情 Tab。
        async closeDetailTabsByDeletedId(options: CloseDetailTabsByDeletedIdOptions) {
            const tabsMenuList = this.tabsMenuList;
            const closedTabs = tabsMenuList.filter(item => isSameDeletedDetailTab(item, options));
            if (!closedTabs.length) return;

            const currentPath = router.currentRoute.value.fullPath || router.currentRoute.value.path;
            const closeCurrent = closedTabs.some(item => item.path === currentPath);
            if (closeCurrent) {
                const currentIndex = tabsMenuList.findIndex(item => item.path === currentPath);
                const nextTab = tabsMenuList[currentIndex + 1] || tabsMenuList[currentIndex - 1];
                if (nextTab) await router.push(nextTab.path);
            }

            this.tabsMenuList = tabsMenuList.filter(item => !closedTabs.some(tab => tab.path === item.path));
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
        },
        // 按照配置模块MAX_TAG_NUMBER删除超出数量的tab
        async removeLimitExceededTabs(currentPath?: string) {
            const maxTagNumber = MAX_TAG_NUMBER;
            if (!maxTagNumber) return;
            const tabsMenuList = this.tabsMenuList;
            this.tabsMenuList = tabsMenuList.slice(-maxTagNumber);
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
            if (currentPath && !this.tabsMenuList.every(item => item.path === currentPath))
                await router.push(this.tabsMenuList[this.tabsMenuList.length - 1].path);
        },
        // Close Tabs On Side
        async closeTabsOnSide(path: string, type: "left" | "right") {
            const currentIndex = this.tabsMenuList.findIndex(item => item.path === path);
            if (currentIndex !== -1) {
                this.tabsMenuList = this.tabsMenuList.filter((item, index) => {
                    const flag = type === "left" ? index >= currentIndex : index <= currentIndex;
                    return flag || !item.close;
                });
            }
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
        },
        // Close MultipleTab
        async closeMultipleTab(tabsMenuValue?: string) {
            this.tabsMenuList = this.tabsMenuList.filter(item => {
                return item.path === tabsMenuValue || !item.close || item.path === router.currentRoute.value.path;
            });
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
        },
        closeAllTab() {
            this.tabsMenuList = [];
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.setKeepAliveName([]);
        },
        // Set Tabs
        async setTabs(tabsMenuList: TabsMenuProps[]) {
            this.tabsMenuList = tabsMenuList;
        },
        async closeTab(route: RouteLocation, callback?: () => void) {
            await this.removeTabs(route.fullPath ?? route.path, callback);
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.removeKeepAliveName(route.name as string);
        },
        connectWebsocket() {
            const user = useUserStore();

            // 清理心跳定时器，保证页面多次进入时只有一个心跳循环。
            const clearHeartbeatTimer = () => {
                if (!timer) return;
                clearInterval(timer);
                timer = undefined;
            };

            // 清理重连定时器，避免 onclose、onerror 和心跳异常同时排队重连。
            const clearReconnectTimer = () => {
                if (!reconnectTimer) return;
                clearTimeout(reconnectTimer);
                reconnectTimer = undefined;
            };

            // 未登录或 token 切换时关闭旧连接，避免旧 token 的 socket 继续触发重连。
            const closeWs = (clearTabs = false) => {
                manualClose = true;
                WS?.close();
                WS = undefined;
                connected = false;
                failure = false;
                currentToken = "";
                reconnectRetries = 0;
                socketOpenedAt = 0;
                clearHeartbeatTimer();
                clearReconnectTimer();
                if (clearTabs) this.closeAllTab();
                manualClose = false;
            };

            // 重连统一从一个入口排队，确保同一时刻最多只有一个重连任务。
            const attemptReconnect = () => {
                if (manualClose || reconnectTimer || !user.token || failure) return;
                if (reconnectRetries >= MAX_RECONNECT_RETRIES) {
                    failure = true;
                    return;
                }
                reconnectRetries++;
                reconnectTimer = setTimeout(() => {
                    reconnectTimer = undefined;
                    connected = false;
                    this.connectWebsocket();
                }, RECONNECT_INTERVAL);
            };

            if (!user.token) {
                closeWs(true);
                return;
            }

            if (currentToken && currentToken !== user.token) {
                closeWs();
            }

            // 已有连接正在建立、已打开或正在关闭时直接复用，防止路由守卫重复创建 WS。
            if (connected && WS?.readyState === WEBSOCKET_READY_STATE.OPEN.code) return;
            if (
                WS &&
                [
                    WEBSOCKET_READY_STATE.CONNECTING.code,
                    WEBSOCKET_READY_STATE.OPEN.code,
                    WEBSOCKET_READY_STATE.CLOSING.code
                ].includes(WS.readyState)
            ) {
                return;
            }

            if (failure) return;

            try {
                clearHeartbeatTimer();
                clearReconnectTimer();
                currentToken = user.token;
                connected = true;
                const auth = `authorization=${user.token}`;
                const socket = new WebSocket(`${import.meta.env.VITE_WS_URL}?${auth}`);
                WS = socket;

                socket.onopen = () => {
                    if (WS !== socket) return;
                    // console.log("建立WS成功---", WS.readyState);
                    // 连接成功后重置重试计数，后续异常再从 0 开始累计。
                    connected = true;
                    socketOpenedAt = Date.now();
                    reconnectRetries = 0;
                    failure = false;
                    clearReconnectTimer();
                };

                socket.onmessage = async (res: MessageEvent) => {
                    if (WS !== socket) return;
                    if (!res?.data) return;
                    const data = JSON.parse(res.data as string);
                    if (data?.type === 1) {
                        await getUserInfo();
                        this.closeAllTab();
                        // 只显示当前页面的tab
                        const currentRoute = router.currentRoute.value;
                        const tabsParams: TabsMenuProps = {
                            icon: currentRoute.meta.icon as string,
                            title: currentRoute.meta.title as string,
                            path: currentRoute.fullPath,
                            name: currentRoute.name as string,
                            close: !currentRoute.meta.isAffix
                        };
                        this.addTabs(tabsParams).then();
                        if (this.nextReload) {
                            // 强制刷新页面
                            window.location.reload();
                        } else {
                            this.setNextReload(true);
                        }
                    }
                    // 客户企业主体发生变更
                    if (data?.type === 4) {
                        await enterChangeDialog();
                    }
                };

                socket.onclose = () => {
                    if (WS !== socket) return;
                    // console.log("WS关闭链接---", event);
                    connected = false;
                    WS = undefined;
                    clearHeartbeatTimer();
                    const stableConnected = socketOpenedAt > 0 && Date.now() - socketOpenedAt >= STABLE_CONNECTION_DURATION;
                    socketOpenedAt = 0;
                    if (!stableConnected) {
                        failure = true;
                        return;
                    }

                    // 连接关闭时尝试重连
                    if (user.token) {
                        // 只有用户已登录时才重连
                        attemptReconnect();
                    }
                };

                socket.onerror = () => {
                    if (WS !== socket) return;
                    // console.log("WS连接错误---", err);
                    connected = false;
                    if (!socketOpenedAt) {
                        failure = true;
                        return;
                    }

                    // 发生错误时尝试重连
                    if (user.token) {
                        attemptReconnect();
                    }
                };

                // 每25秒发送一个心跳信息
                const loopConnection = () => {
                    timer = setInterval(() => {
                        if (WS === socket && socket.readyState === WEBSOCKET_READY_STATE.OPEN.code) {
                            try {
                                socket.send(JSON.stringify({ heartBeat: "HEARTBEAT" }));
                            } catch {
                                // console.log("心跳发送失败", error);
                                // 心跳发送失败也尝试重连
                                attemptReconnect();
                            }
                        } else {
                            connected = false;
                            // WebSocket状态不正常时尝试重连
                            attemptReconnect();
                        }
                    }, 25000);
                };

                loopConnection();
            } catch (err) {
                failure = true;
                connected = false;
                console.error("WS连接异常---", err);

                // 捕获异常时也尝试重连
                if (user.token) {
                    attemptReconnect();
                }
            }
        },

        setNextReload(nextReload: boolean) {
            this.nextReload = nextReload;
        }
    }
    // persist: piniaPersistConfig("zhongsheng-tabs")
});
